// Seção "Sobre": versão, commit do build e o que mudou. Aparece no guia (?) e no CMS.
//
// Os dados vêm de GET /api/sobre (com login), buscados uma vez por página. Enquanto não chegam,
// a seção diz "carregando"; se a busca falhar, diz que não deu para ler e tenta de novo na
// próxima vez que a seção for pedida. Os textos ficam aqui nos quatro idiomas, como os do guia.

import { normalizaSobre, type Sobre } from '../core/sobre.js';
import { getLang, localeTag, type Lang } from './i18n.js';

type Estado = Sobre | 'carregando' | 'erro';

const TEXTOS: Record<Lang, { titulo: string; versao: string; commit: string; build: string; mudou: string; vazio: string; carregando: string; erro: string }> = {
  pt: {
    titulo: 'Sobre',
    versao: 'Versão',
    commit: 'Commit',
    build: 'Build de',
    mudou: 'O que mudou',
    vazio: 'Sem histórico de commits neste build.',
    carregando: 'carregando…',
    erro: 'Não deu para ler os dados do build.',
  },
  en: {
    titulo: 'About',
    versao: 'Version',
    commit: 'Commit',
    build: 'Build from',
    mudou: 'What changed',
    vazio: 'No commit history in this build.',
    carregando: 'loading…',
    erro: "Couldn't read the build data.",
  },
  es: {
    titulo: 'Acerca de',
    versao: 'Versión',
    commit: 'Commit',
    build: 'Build de',
    mudou: 'Qué cambió',
    vazio: 'Sin historial de commits en este build.',
    carregando: 'cargando…',
    erro: 'No se pudieron leer los datos del build.',
  },
  fr: {
    titulo: 'À propos',
    versao: 'Version',
    commit: 'Commit',
    build: 'Build du',
    mudou: 'Ce qui a changé',
    vazio: "Pas d'historique de commits dans ce build.",
    carregando: 'chargement…',
    erro: 'Impossible de lire les données du build.',
  },
};

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (valor: string): string => valor.replace(/[&<>"']/g, (c) => ESC[c] ?? c);

let estado: Estado = 'carregando';
let pedido: Promise<void> | undefined;

/** Busca o Sobre (uma vez; depois de uma falha, a próxima chamada tenta de novo). */
export function carregaSobre(): Promise<void> {
  if (!pedido) {
    pedido = fetch('/api/sobre', { credentials: 'same-origin' })
      .then((resposta) => {
        if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
        return resposta.json() as Promise<unknown>;
      })
      .then((corpo) => {
        estado = normalizaSobre(corpo);
      })
      .catch(() => {
        estado = 'erro';
        pedido = undefined;
      });
  }
  return pedido;
}

/** Corpo da seção (dados e lista do que mudou), sem título: o guia e o CMS põem o título deles. */
export function corpoSobre(lang: Lang, atual: Estado = estado): string {
  const x = TEXTOS[lang];
  if (atual === 'carregando') return `<p class="sobre-aviso">${esc(x.carregando)}</p>`;
  if (atual === 'erro') return `<p class="sobre-aviso erro">${esc(x.erro)}</p>`;
  const data = atual.data ? new Date(atual.data) : undefined;
  const quando = data && !Number.isNaN(data.getTime()) ? data.toLocaleString(localeTag(), { dateStyle: 'short', timeStyle: 'short' }) : '';
  const linhas = atual.mudou.length
    ? `<ul class="sobre-mudou">${atual.mudou.map((linha) => `<li>${esc(linha)}</li>`).join('')}</ul>`
    : `<p class="sobre-aviso">${esc(x.vazio)}</p>`;
  // uma linha de texto (com "·" entre os itens): legível também no CMS, que não carrega o style.css
  return (
    `<p class="sobre-dados">${esc(x.versao)} <b>${esc(atual.versao)}</b> · ${esc(x.commit)} <code>${esc(atual.commit)}</code>` +
    (quando ? ` · ${esc(x.build)} <b>${esc(quando)}</b>` : '') +
    `</p><h4 class="guia-sub">${esc(x.mudou)}</h4>${linhas}`
  );
}

/** A seção inteira do guia (mesmas classes das outras seções). Lê o estado do momento. */
export function sobreSecaoGuia(lang: Lang = getLang()): string {
  return `<section class="guia-sec" aria-labelledby="guia-sobre"><h3 id="guia-sobre">${esc(TEXTOS[lang].titulo)}</h3>${corpoSobre(lang)}</section>`;
}
